import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { api, resolveImage } from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import Seo from "@/components/Seo";

const CATEGORY_ACCENT = {
  "Brand Activation": "bg-ot-peach",
  "Event Technology": "bg-ot-mint",
  "IT & AV Rental": "bg-ot-coral",
};

export default function Products() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "All";
  usePageTitle("products");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/products");
        if (!cancelled) setItems(data);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return ["All", ...Array.from(set)];
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((p) => {
      const catOk = activeCategory === "All" || p.category === activeCategory;
      const q = search.trim().toLowerCase();
      const searchOk = !q || p.name.toLowerCase().includes(q) || p.short_description.toLowerCase().includes(q);
      return catOk && searchOk;
    });
  }, [items, activeCategory, search]);

  const setCategory = (c) => {
    if (c === "All") setSearchParams({});
    else setSearchParams({ category: c });
  };

  return (
    <div data-testid="products-page">
      <Seo
        title="Event Tech Products — Badging, Registration, Photobooth, Voting | Belgium"
        path="/products"
        description="OneTec's catalog of 18+ production-ready event technology products available in Belgium and across Europe: online registration, onsite badging, event accreditation systems, photobooth rental (Vespa, 120°, Studio from the Sky), AI face swap, VR experiences, mobile voting, lead trackers and more."
      />
      {/* HEADER */}
      <section className="border-b-2 border-ot-fg">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <p className="text-xs uppercase tracking-widest font-bold text-ot-indigo">Products</p>
          <h1 className="font-display text-6xl md:text-[9vw] uppercase tracking-tighter leading-[0.88] mt-4">
            The product box.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ot-fg/80">
            {items.length} production-ready products you can deploy at your next event. Filter, pick, brief us — we'll do the rest.
          </p>

          {/* Filters */}
          <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex flex-wrap gap-2" data-testid="product-filters">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`px-4 py-2 border-2 border-ot-fg font-display uppercase text-sm transition-all ${
                    activeCategory === c
                      ? "bg-ot-indigo text-white"
                      : "bg-white hover:bg-ot-peach"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products..."
              className="nb-input max-w-sm lg:ml-auto"
              data-testid="product-search"
            />
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="nb-card h-96 animate-pulse bg-white/50" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-20 text-xl" data-testid="no-products">No products match.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-testid="products-grid">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: (i % 6) * 0.05, duration: 0.4 }}
              >
                <Link to={`/products/${p.slug}`} className="block nb-card group overflow-hidden" data-testid={`product-card-${p.slug}`}>
                  <div className={`${CATEGORY_ACCENT[p.category] || "bg-ot-muted"} border-b-2 border-ot-fg relative aspect-[4/3] overflow-hidden`}>
                    <img
                      src={resolveImage(p.image_url)}
                      alt={p.name}
                      className="w-full h-full object-cover mix-blend-multiply group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <span className={`absolute top-3 left-3 ${CATEGORY_ACCENT[p.category] || "bg-ot-muted"} text-ot-fg text-xs font-bold uppercase tracking-wider px-2 py-1 border-2 border-ot-fg`}>
                      {p.category}
                    </span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-2xl uppercase leading-tight">{p.name}</h3>
                      <ArrowUpRight weight="bold" size={22} className="mt-1 shrink-0 group-hover:rotate-45 transition-transform" />
                    </div>
                    <p className="mt-2 text-sm text-ot-fg/70">{p.short_description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
